import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { NgApexchartsModule } from 'ng-apexcharts';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { DashboardService } from '../../../core/services/dashboard.service';
import { ActivityHeatmapData, ActivityHeatmapResponse } from '../../../core/models/dashboard.model';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-ui-activity-heatmap',
  standalone: true,
  imports: [CommonModule, NgApexchartsModule, LoadingSpinnerComponent],
  templateUrl: './ui-activity-heatmap.component.html',
  styleUrl: './ui-activity-heatmap.component.scss'
})
export class UiActivityHeatmapComponent implements OnInit, OnDestroy {
  public activityData: ActivityHeatmapData[] = [];
  public loading = false;
  public error = false;
  public chartOptions: any;

  private destroy$ = new Subject<void>();

  constructor(
    private dashboardService: DashboardService,
    private authService: AuthService
  ) {
    this.initializeChartOptions();
  }

  ngOnInit(): void {
    this.loadActivityHeatmap();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Inicializa as opções do gráfico com valores padrão
   */
  private initializeChartOptions(): void {
    this.chartOptions = {
      series: [],
      chart: {
        height: 350,
        type: 'heatmap',
        toolbar: {
          show: false,
        },
        events: {
          mounted: (chart: any) => {
            chart.windowResizeHandler();
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      colors: ['#60A5FA'],
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.5,
          radius: 0,
          useFillColorAsStroke: true,
          colorScale: {
            ranges: [
              {
                from: 0,
                to: 5,
                name: 'baixa',
                color: '#60A5FA'  // Azul frio (baixa atividade)
              },
              {
                from: 6,
                to: 15,
                name: 'média',
                color: '#FCD34D'  // Amarelo claro (atividade média)
              },
              {
                from: 16,
                to: 30,
                name: 'alta',
                color: '#FB923C'  // Laranja (alta atividade)
              },
              {
                from: 31,
                to: 100,
                name: 'muito alta',
                color: '#EF4444'  // Vermelho quente (atividade muito alta)
              }
            ]
          }
        }
      },
      title: {
        text: '',
      },
      xaxis: {
        type: 'category',
        categories: this.generateHourCategories(),
        title: {
          text: '',
        },
        labels: {
          show: true,
          style: {
            colors: '#8c9097',
            fontSize: '11px',
            fontWeight: 600,
            cssClass: 'apexcharts-xaxis-label',
          },
        },
      },
      yaxis: {
        title: {
          text: '',
        },
        labels: {
          show: true,
          style: {
            colors: '#8c9097',
            fontSize: '11px',
            fontWeight: 600,
            cssClass: 'apexcharts-yaxis-label',
          },
        },
      },
      tooltip: {
        custom: function({series, seriesIndex, dataPointIndex, w}: any) {
          const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
          const day = dayNames[seriesIndex];
          const hour = w.globals.labels[dataPointIndex];
          const count = series[seriesIndex][dataPointIndex];
          
          return `
            <div class="px-3 py-2">
              <div class="font-semibold text-gray-800">${day} - ${hour}h</div>
              <div class="text-sm text-gray-600">Atividades: ${count}</div>
            </div>
          `;
        }
      },
      grid: {
        borderColor: '#e7e7e7',
        row: {
          colors: ['#f3f3f3', 'transparent'],
          opacity: 0.5,
        },
      },
    };
  }

  /**
   * Gera as categorias de horas (0-23)
   */
  private generateHourCategories(): string[] {
    return Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}h`);
  }

  /**
   * Carrega os dados do heatmap de atividades
   */
  public loadActivityHeatmap(): void {
    // Verificar se o usuário está autenticado
    if (!this.authService.isAuthenticated()) {
      console.warn('Usuário não autenticado. Não é possível carregar heatmap de atividades.');
      this.error = true;
      return;
    }

    this.loading = true;
    this.error = false;

    this.dashboardService.getActivityHeatmap(30)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.activityData = response.data;
            this.updateChartData(response.data, response.legend.days);
            this.loading = false;
          } else {
            console.warn('Resposta da API sem dados válidos:', response);
            this.error = true;
            this.loading = false;
          }
        },
        error: (error) => {
          console.error('Erro ao carregar heatmap de atividades:', error);
          this.handleApiError(error);
          this.loading = false;
        }
      });
  }

  /**
   * Atualiza os dados do gráfico com base nos dados de atividade recebidos
   * @param activityData Lista de dados de atividade da API
   * @param dayNames Array com os nomes dos dias da semana
   */
  private updateChartData(activityData: ActivityHeatmapData[], dayNames: string[]): void {
    // Criar matriz 7x24 (dias x horas) inicializada com 0
    const heatmapMatrix: number[][] = Array(7).fill(null).map(() => Array(24).fill(0));
    
    // Preencher a matriz com os dados da API
    activityData.forEach(item => {
      if (item.day >= 0 && item.day < 7 && item.hour >= 0 && item.hour < 24) {
        heatmapMatrix[item.day][item.hour] = item.count;
      }
    });

    // Criar series para o ApexCharts
    const series = dayNames.map((dayName, dayIndex) => ({
      name: dayName,
      data: heatmapMatrix[dayIndex].map((count, hourIndex) => ({
        x: `${hourIndex.toString().padStart(2, '0')}h`,
        y: count
      }))
    }));

    this.chartOptions = {
      ...this.chartOptions,
      series: series,
      yaxis: {
        ...this.chartOptions.yaxis,
        categories: dayNames,
      },
    };
  }


  /**
   * Trata erros específicos da API
   * @param error Erro recebido da API
   */
  private handleApiError(error: any): void {
    this.error = true;
    
    // Log específico baseado no tipo de erro
    if (error.status === 401) {
      console.warn('Token não fornecido, inválido ou expirado');
    } else if (error.status === 500) {
      console.error('Erro interno do servidor');
    } else {
      console.error('Erro desconhecido ao carregar heatmap de atividades:', error);
    }
  }


  /**
   * Calcula o total de atividades
   * @returns Total de atividades
   */
  public getTotalActivities(): number {
    return this.activityData.reduce((total, item) => total + item.count, 0);
  }
}
